import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ChevronDown, Percent } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { MultiSelect } from '@/components/ui/multi-select';
import { Skeleton } from '@/components/ui/skeleton';
import { StatTile } from '@/components/ui/stat-tile';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LocalizedName, localizedName } from '@/components/localized-name';
import { useBusinesses } from '@/features/business/use-businesses';
import {
  formatCurrency,
  formatDateTime,
  formatNumber,
  initials,
  toPercent,
} from '@/lib/format';
import { storeToday } from '@/lib/store-time';
import { cn } from '@/lib/utils';
import { useDiscountUsage, usePosDiscounts } from './pos-discounts.api';
import {
  DISCOUNT_TYPE,
  takesValue,
  type DiscountUsage,
} from './types';

/**
 * Where the discounts actually went.
 *
 * Was `Reports > POS Discounts`, a separate page whose only filter was a list
 * of the very records the other tab maintains. Keeping them apart meant
 * checking whether a discount was switched on, then navigating to a different
 * section of the menu to see whether anyone had used it.
 */

const ANY = 'ANY';

function currentMonth() {
  const today = storeToday();
  return { start: `${today.slice(0, 7)}-01`, end: today };
}

export function UsageTab() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';

  const [range, setRange] = useState(currentMonth);
  const [lifetime, setLifetime] = useState('false');
  const [discountIds, setDiscountIds] = useState<string[]>([]);
  const [businessId, setBusinessId] = useState(ANY);
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data: discounts } = usePosDiscounts();
  const { data: businesses } = useBusinesses();

  const report = useDiscountUsage(
    {
      start: range.start,
      end: range.end,
      lifetime: lifetime === 'true',
      discountIds,
      businessId: businessId === ANY ? null : businessId,
    },
    true
  );

  const rows = report.data?.discounts ?? [];
  const isLifetime = lifetime === 'true';

  const fields: FilterField[] = [
    {
      id: 'discounts',
      label: t('posDiscounts.discounts'),
      value: discountIds.join(','),
      onChange: (value) => setDiscountIds(value ? value.split(',') : []),
      resetValue: '',
      render: ({ id, disabled }) => (
        <MultiSelect
          id={id}
          icon={Percent}
          heading={t('posDiscounts.discounts')}
          allLabel={t('posDiscounts.allDiscounts')}
          summarise={(count) => t('posDiscounts.nDiscounts', { count })}
          disabled={disabled}
          selected={discountIds}
          onChange={setDiscountIds}
          options={(discounts ?? []).map((discount) => ({
            value: discount.id,
            label: localizedName(discount, language),
          }))}
        />
      ),
    },
    {
      id: 'store',
      label: t('posDiscounts.store'),
      value: businessId,
      onChange: setBusinessId,
      options: [
        { value: ANY, label: t('posDiscounts.allStores') },
        ...(businesses ?? []).map((business) => ({
          value: business.id,
          label: business.name,
        })),
      ],
    },
    {
      id: 'lifetime',
      label: t('posDiscounts.period'),
      value: lifetime,
      onChange: setLifetime,
      sort: false,
      resetValue: 'false',
      options: [
        { value: 'false', label: t('posDiscounts.dateRange') },
        { value: 'true', label: t('posDiscounts.lifetime') },
      ],
    },
    // Hidden while Lifetime is on, where a range decides nothing. The original
    // left both pickers live and let people wonder why the dates did nothing.
    ...(isLifetime
      ? []
      : ([
          {
            id: 'start',
            label: t('posDiscounts.from'),
            type: 'date',
            value: range.start,
            resetValue: range.start,
            onChange: (value: string) =>
              setRange((current) => ({ ...current, start: value })),
          },
          {
            id: 'end',
            label: t('posDiscounts.to'),
            type: 'date',
            value: range.end,
            resetValue: range.end,
            onChange: (value: string) =>
              setRange((current) => ({ ...current, end: value })),
          },
        ] as FilterField[])),
  ];

  const valueLabel = (row: DiscountUsage) => {
    if (!takesValue(row.discountType)) return t(`posDiscounts.type${row.discountType}`);
    return row.discountType === DISCOUNT_TYPE.percentageAmount
      ? `${formatNumber(toPercent(row.discountValue))}%`
      : formatCurrency(row.discountValue);
  };

  return (
    <div className="space-y-4 pt-4">
      <FilterBar busy={report.isFetching} fields={fields} />

      <div className="grid grid-cols-2 gap-2 lg:grid-cols-3">
        <StatTile
          label={t('posDiscounts.totalGiven')}
          value={formatCurrency(report.data?.totalAmount ?? 0)}
          tone="bad"
          loading={report.isPending}
          busy={report.isFetching}
        />
        <StatTile
          label={t('posDiscounts.ordersWithDiscount')}
          value={formatNumber(report.data?.total ?? 0)}
          loading={report.isPending}
          busy={report.isFetching}
        />
        <StatTile
          label={t('posDiscounts.customers')}
          value={formatNumber(report.data?.totalCustomers ?? 0)}
          loading={report.isPending}
          busy={report.isFetching}
        />
      </div>

      {report.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('posDiscounts.reportFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void report.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {report.isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="flex items-center gap-4 px-4 py-3">
              <Skeleton className="h-4 flex-1" />
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-12" />
            </div>
          ))}
        </div>
      )}

      {report.isSuccess && rows.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={report.isFetching} />
          <p className="text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm">
            {t('posDiscounts.noUsage')}
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="rounded-xl border">
          <Table busy={report.isFetching}>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-8" />
                <TableHead>{t('posDiscounts.name')}</TableHead>
                <TableHead>{t('posDiscounts.value')}</TableHead>
                <TableHead className="text-right">
                  {t('posDiscounts.orders')}
                </TableHead>
                <TableHead className="text-right">
                  {t('posDiscounts.given')}
                </TableHead>
                <TableHead className="text-right">
                  {t('posDiscounts.orderTotal')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const open = expanded === row.id;
                const orders = row.orders ?? [];
                return (
                  <Fragment key={row.id}>
                    <TableRow
                      className={cn(
                        'cursor-pointer',
                        open && 'bg-muted/40 border-b-0'
                      )}
                      aria-expanded={open}
                      onClick={() => setExpanded(open ? null : row.id)}
                    >
                      <TableCell>
                        <ChevronDown
                          className={cn(
                            'text-muted-foreground size-4 transition-transform',
                            open && 'rotate-180'
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <LocalizedName nameEn={row.nameEn} nameEs={row.nameEs} />
                      </TableCell>
                      <TableCell className="text-xs">
                        {valueLabel(row)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatNumber(row.orderCount)}
                      </TableCell>
                      <TableCell className="text-right font-medium tabular-nums">
                        {formatCurrency(row.appliedAmount)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(row.orderTotal)}
                      </TableCell>
                    </TableRow>

                    {open && (
                      <TableRow className="hover:bg-transparent">
                        <TableCell colSpan={6} className="bg-muted/40 p-3">
                          {orders.length === 0 ? (
                            <p className="text-muted-foreground py-4 text-center text-sm">
                              {t('posDiscounts.noOrders')}
                            </p>
                          ) : (
                            <div className="bg-card divide-y rounded-lg border">
                              {orders.map((order) => (
                                <div
                                  key={order.id}
                                  className="flex items-center gap-3 p-2.5 text-sm"
                                >
                                  <Avatar className="size-7 shrink-0">
                                    {order.customerAvatar && (
                                      <AvatarImage
                                        src={order.customerAvatar}
                                        alt=""
                                      />
                                    )}
                                    <AvatarFallback>
                                      {initials(
                                        order.customerName?.split(' ')[0],
                                        order.customerName?.split(' ')[1]
                                      )}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div className="min-w-0 flex-1">
                                    <p className="truncate">
                                      {order.customerName || '—'}
                                    </p>
                                    <p className="text-muted-foreground truncate text-xs">
                                      <span className="font-mono">
                                        {order.confirmationCode}
                                      </span>
                                      {' · '}
                                      {formatDateTime(order.createdAt)}
                                    </p>
                                  </div>
                                  {order.employeeName && (
                                    <Badge variant="secondary">
                                      {order.employeeName}
                                    </Badge>
                                  )}
                                  <div className="shrink-0 text-right">
                                    <p className="tabular-nums">
                                      {formatCurrency(order.amountApplied)}
                                    </p>
                                    <p className="text-muted-foreground text-xs tabular-nums">
                                      {formatCurrency(order.orderTotal)}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <p className="text-muted-foreground text-xs">
        {t('posDiscounts.reportNote', {
          scope: isLifetime
            ? t('posDiscounts.lifetime').toLowerCase()
            : `${range.start} → ${range.end}`,
        })}
      </p>

    </div>
  );
}
